import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Text,
  UnorderedList,
  ListItem,
  Code,
} from '@chakra-ui/react';

export const EditorHelp = ({ isOpen, onClose }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="#17458f">Email Template Editor Help</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          {/* Introduction Section */}
          <Box mb={6}>
            <Text mb={3}>
              The email template editor allows you to create and manage email templates with both HTML and text versions.
              You can use variables to personalize your emails and preview how they will look before sending.
            </Text>
          </Box>

          <Accordion allowMultiple>
            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left" fontWeight="medium">
                  Template Variables
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <Text mb={3}>
                  Template variables allow you to personalize your emails. When the email is sent,
                  these variables will be replaced with actual values.
                </Text>
                <UnorderedList spacing={2}>
                  <ListItem><Code>{"{{firstName}}"}</Code> - Recipient's first name</ListItem>
                  <ListItem><Code>{"{{lastName}}"}</Code> - Recipient's last name</ListItem>
                  <ListItem><Code>{"{{fullName}}"}</Code> - Recipient's full name</ListItem>
                  <ListItem><Code>{"{{phone}}"}</Code> - Recipient's phone number</ListItem>
                  <ListItem><Code>{"{{email}}"}</Code> - Recipient's email address</ListItem>
                  <ListItem><Code>{"{{clubName}}"}</Code> - Name of the Rotary club</ListItem>
                  <ListItem>
                    <Code>{"{{sendDate}}"}</Code> - The date (e.g., "December 27, 2024")
                    <UnorderedList ml={4} mt={1}>
                      <ListItem><Code>{"{{sendDate:full}}"}</Code> - Full date with day (e.g., "Friday, December 27, 2024")</ListItem>
                      <ListItem><Code>{"{{sendDate:long}}"}</Code> - Long date (e.g., "December 27, 2024")</ListItem>
                      <ListItem><Code>{"{{sendDate:medium}}"}</Code> - Medium date (e.g., "Dec 27, 2024")</ListItem>
                      <ListItem><Code>{"{{sendDate:short}}"}</Code> - Short date (e.g., "12/27/2024")</ListItem>
                    </UnorderedList>
                  </ListItem>
                </UnorderedList>
                <Text mt={3}>
                  To insert a variable, click on the variable button above the editor when your cursor
                  is where you want the variable to appear. For the send date, you can choose the date format
                  when inserting the variable.
                </Text>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left" fontWeight="medium">
                  HTML and Text Versions
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <Text mb={3}>
                  Each email template has both HTML and text versions. The HTML version can include
                  formatting, while the text version is plain text only.
                </Text>
                <UnorderedList spacing={2}>
                  <ListItem>
                    <Text fontWeight="medium">HTML Version</Text>
                    Use this for formatted emails with styles, links, and other HTML elements. 
                    Most modern email clients will display this version.
                  </ListItem>
                  <ListItem>
                    <Text fontWeight="medium">Text Version</Text>
                    A plain text fallback that will be used if the recipient's email client 
                    doesn't support HTML or if they prefer plain text.
                  </ListItem>
                </UnorderedList>
                <Text mt={3}>
                  <strong>Convert to Plain Text:</strong> This button will automatically create a text version
                  from your HTML content, stripping out formatting while preserving the structure.
                  The button is enabled when you make changes to the HTML version.
                </Text>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left" fontWeight="medium">
                  Preview and Testing
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <Text mb={3}>
                  Before sending your email, you can preview how it will look and send test emails
                  to verify everything works correctly.
                </Text>
                <UnorderedList spacing={2}>
                  <ListItem>
                    <Text fontWeight="medium">Preview Button</Text>
                    Shows how your email will look with example variable values. 
                    You can preview both HTML and text versions.
                  </ListItem>
                  <ListItem>
                    <Text fontWeight="medium">Test Settings</Text>
                    In the main template list, you can configure:
                    <UnorderedList mt={2}>
                      <ListItem>Test Email: The email address to receive the test</ListItem>
                      <ListItem>Test Name: Used to fill in name variables</ListItem>
                      <ListItem>Club Name: Used for the club name variable</ListItem>
                    </UnorderedList>
                  </ListItem>
                </UnorderedList>
              </AccordionPanel>
            </AccordionItem>

            <AccordionItem>
              <AccordionButton>
                <Box flex="1" textAlign="left" fontWeight="medium">
                  Best Practices
                </Box>
                <AccordionIcon />
              </AccordionButton>
              <AccordionPanel>
                <UnorderedList spacing={2}>
                  <ListItem>Always maintain both HTML and text versions of your email</ListItem>
                  <ListItem>Test your email with different variable values to ensure it reads well</ListItem>
                  <ListItem>Preview both HTML and text versions before sending</ListItem>
                  <ListItem>Keep your HTML simple and compatible with email clients</ListItem>
                  <ListItem>Use variables consistently to maintain a personal touch</ListItem>
                  <ListItem>Send test emails to verify the final appearance</ListItem>
                </UnorderedList>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
